import ApiService from "@/services/api.service";
import JwtService from "@/services/jwt.service";
import router from "@/router";
import { decryptAesGcm } from "../utils/crypto";

const state = {
  jwt: { avatar: "", title: null, token: "", username: "" },
  isAuthenticated: null,
  otpUrl: null,
  otpVerified: false,
  user: null,
  currentRole: parseInt(localStorage.getItem("currentRole")) || 1,
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  getCurrentRole(state) {
    return state.currentRole;
  },
  isChatOperator(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(16384);
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  getOtpUrl(state) {
    return state.otpUrl;
  },
  getOtpVerified(state) {
    return state.otpVerified;
  },
  isArgeUser(state) {
    return state.user?.email == "arge@barikat.com";
  },
  isSuperUser(state) {
    return state.user?.is_superuser;
  },
  isSuperHr(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(32768);
  },
  isHr(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(8192);
  },
  isCustomer(state) {
    const user = state.user;
    return user?.type ? user.type === 3 : null;
  },
  isDispatcher(state) {
    const roles =
      (state.user &&
        state.user?.[state.currentRole === 1 ? "roles" : "roles2"]) ||
      [];
    return roles.includes(131072) || state.user?.is_superuser;
  },
  isOnlyDispatcher(state) {
    const roles =
      (state.user &&
        state.user?.[state.currentRole === 1 ? "roles" : "roles2"]) ||
      [];
    return roles.includes(131072);
  },
  isProjectManager(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(512) || state.user?.is_superuser;
  },
  isOnlyProjectManager(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(512);
  },
  isProviderEmployee(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(1024) || state.user?.is_superuser;
  },
  isProviderUnitManager(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(32) || state.user?.is_superuser;
  },
  isProviderAUnitManager(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(64) || state.user?.is_superuser;
  },
  isProviderTeamLeader(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(128) || state.user?.is_superuser;
  },
  isProviderManager(state) {
    const roles =
      state.user?.[state.currentRole === 1 ? "roles" : "roles2"] || [];
    return roles.includes(16) || state.user?.is_superuser;
  },
  hasPermission(state) {
    return (module, method = "read") => {
      if (!module) {
        return false;
      }
      const currentUser = state.user;
      if (!currentUser) {
        return false;
      }
      if (currentUser.is_superuser) {
        return true;
      }
      if (
        currentUser?.[
          state.currentRole === 1 ? "permissions" : "permissions2"
        ] &&
        currentUser?.[state.currentRole === 1 ? "permissions" : "permissions2"][
          method
        ] &&
        currentUser?.[state.currentRole === 1 ? "permissions" : "permissions2"][
          method
        ].includes(module)
      ) {
        return true;
      }
      return false;
    };
  },
  hasPermissionChild(state) {
    return (module, method = "read", comingFrom) => {
      if (!module) {
        return false;
      }
      const currentUser = state.user;
      if (!currentUser) {
        return false;
      }
      if (currentUser.is_superuser) {
        return true;
      }
      if (
        currentUser?.[
          state.currentRole === 1 ? "permissions" : "permissions2"
        ] &&
        currentUser?.[state.currentRole === 1 ? "permissions" : "permissions2"][
          method
        ] &&
        currentUser?.[state.currentRole === 1 ? "permissions" : "permissions2"][
          method
        ].includes(comingFrom + module)
      ) {
        return true;
      }
      return false;
    };
  },
};

const actions = {
  fetchOtp(context, data) {
    return new Promise((resolve, reject) => {
      ApiService.post("getotploginurl/", data)
        .then(({ data }) => {
          if (data.otp_url) {
            context.commit("setOtpUrl", data.otp_url);
          } else if (data.otp_verified) {
            context.commit("setOtpVerified", data.otp_verified);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  checkOtp(context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post("check-otp/", credentials)
        .then(({ data }) => {
          context.commit("SET_TOKEN", data);
          resolve();
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },

  logOut(context) {
    ApiService.post("logout/", { refresh: JwtService.getRefresh() }).then(
      () => {
        router.push("/login").then(() => {
          context.commit("PURGE_AUTH");
        });
      },
    );
  },
  fetchCurrentUser(context) {
    return new Promise((resolve, reject) => {
      ApiService.post("currentuser/")
        .then(async ({ data }) => {
          const [encryptedData, nonce] = data.split("|");
          const decryptedData = JSON.parse(
            await decryptAesGcm(
              { data: encryptedData, nonce: nonce },
              {
                aesSecretKey: "NpjHhGDr4omMaE53ouzFv4t5B5Sn2yPvZ4nTbM3GWFW=",
              },
            ),
          );
          context.commit("SET_CURRENT_USER", decryptedData);
          resolve(decryptedData);
        })
        .catch(({ response }) => {
          const data = response?.data;
          if (data?.code === "token_not_valid") {
            context.commit("PURGE_AUTH");
            if (router.currentRoute.path !== "/login") {
              router.push("/login");
            }
          }
          reject(response);
        });
    });
  },
  changeLoggedUser(context, userId) {
    return new Promise((resolve) => {
      ApiService.post("changeloggeduser/", { user_id: userId }).then(
        ({ data }) => {
          context.commit("SET_TOKEN", data);
          resolve();
        },
      );
    });
  },
};

const mutations = {
  setOtpUrl(state, payload) {
    state.otpUrl = payload;
  },
  setOtpVerified(state, payload) {
    state.otpVerified = payload;
  },
  SET_TOKEN(state, payload) {
    state.jwt = payload;
    JwtService.saveToken(state.jwt.access);
    JwtService.saveRefresh(state.jwt.refresh);
    state.errors = {};
    state.isAuthenticated = true;
  },
  SET_CURRENT_USER(state, payload) {
    localStorage.setItem("employeeId", payload.employee?.id);
    state.user = payload;
    state.isAuthenticated = true;
  },
  SET_PASSWORD(state, password) {
    state.user.password = password;
  },
  PURGE_AUTH(state) {
    state.isAuthenticated = false;
    state.user = {};
    state.otpVerified = false;
    JwtService.destroyToken();
    state.errors = {};
  },
  SET_CURRENT_ROLE(state) {
    state.currentRole = state.currentRole === 1 ? 2 : 1;
    localStorage.setItem("currentRole", state.currentRole);
    router.push("/").finally(() => {
      window.location.reload();
    });
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
